import { useState } from "react";
import { SuiteAppTile, type SuiteAppId } from "@athavita/ui-theme";
import type { DriveItem } from "@/api/driveApi";
import { CATEGORY_STYLE, categoryOf, type FileCategory } from "@/lib/fileTypes";
import { cn } from "@/lib/utils";
import { hasServerThumbnail, isDocumentThumbnail, useThumbnail } from "./thumbnails";

type IconItem = Pick<DriveItem, "kind" | "name" | "contentType">;

/** Type-coloured Lucide icon (blue docs, green sheets, orange slides, red PDFs…). */
export function ItemIcon({ item, className }: { item: IconItem; className?: string }) {
    const style = CATEGORY_STYLE[categoryOf(item)];
    const Icon = style.icon;
    return <Icon className={cn("h-5 w-5 shrink-0", style.color, className)} aria-hidden />;
}

/** The suite app that opens a file type — its tile is the file's identity. */
const APP_TILE: Partial<Record<FileCategory, SuiteAppId>> = {
    document: "docs",
    spreadsheet: "sheets",
    presentation: "slides",
    pdf: "pdf"
};

/**
 * Square app tile (Docs blue, Sheets green, Slides orange, PDF red) for
 * Office files and PDFs; a neutral tile with the type icon for anything
 * else; a plain folder icon for folders.
 */
export function FileTile({ item, size = 24 }: { item: IconItem; size?: number }) {
    const cat = categoryOf(item);
    const app = APP_TILE[cat];
    if (app) return <SuiteAppTile app={app} size={size} />;
    if (cat === "folder") return <TypeIcon item={item} px={Math.round(size * 0.84)} />;
    return (
        <span
            aria-hidden
            className="inline-flex shrink-0 items-center justify-center bg-[var(--drive-subtle)]"
            style={{ width: size, height: size, borderRadius: Math.round(size / 4.5) }}
        >
            <TypeIcon item={item} px={Math.round(size * 0.62)} />
        </span>
    );
}

function TypeIcon({ item, px }: { item: IconItem; px: number }) {
    const style = CATEGORY_STYLE[categoryOf(item)];
    const Icon = style.icon;
    return <Icon className={cn("shrink-0", style.color)} style={{ width: px, height: px }} aria-hidden />;
}

type ThumbSize = "row" | "card" | "hero" | "suggest";

/**
 * Thumbnail (lazy, cached) with a type fallback until the BFF has made one.
 * `row` = 28 px list cell (photos only — other types show their app tile),
 * `card` = grid card, `hero` = details pane, `suggest` = Suggested file card.
 */
export function ItemThumb({ item, size }: { item: DriveItem; size: ThumbSize }) {
    const cat = categoryOf(item);
    const { ref, src } = useThumbnail<HTMLDivElement>(item, hasServerThumbnail(item) && (size !== "row" || cat === "image"));
    const [broken, setBroken] = useState(false);
    const style = CATEGORY_STYLE[cat];
    const showImg = Boolean(src) && !broken;
    // A page preview reads from the top (like Google Drive's cards); a photo is centred.
    const isPage = isDocumentThumbnail(item);

    if (size === "row") {
        return (
            <div ref={ref} className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded">
                {showImg ? (
                    <img src={src ?? undefined} alt="" draggable={false} className="h-full w-full rounded object-cover" onError={() => setBroken(true)} />
                ) : (
                    <FileTile item={item} size={24} />
                )}
            </div>
        );
    }

    const box = size === "card" ? "aspect-[4/3] rounded-lg" : size === "hero" ? "h-[170px]" : "h-[104px]";
    return (
        <div
            ref={ref}
            className={cn(
                "flex w-full items-center justify-center overflow-hidden",
                box,
                size === "card" && !showImg ? style.tint : "bg-[var(--drive-subtle)]"
            )}
        >
            {showImg ? (
                <img
                    src={src ?? undefined}
                    alt=""
                    draggable={false}
                    className={cn("h-full w-full", size === "hero" ? "object-contain" : isPage ? "object-cover object-top" : "object-cover")}
                    onError={() => setBroken(true)}
                />
            ) : size === "card" ? (
                <ItemIcon item={item} className="h-12 w-12" />
            ) : (
                <FileTile item={item} size={size === "hero" ? 56 : 40} />
            )}
        </div>
    );
}
